// client/src/hooks/useOptimizedStoryData.ts
// PHASE 1: Intelligent API Loading Hook with Enhanced Blueprint Caching Strategy

import { useQuery } from '@tanstack/react-query';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { MainView, BlueprintState, OptimizedDataLoading } from '@/writer/types';
// ⚠️ BREAKING CYCLES: Inline critical utilities to prevent temporal dead zone
// DO NOT import shared utilities that story-workspace.tsx also imports

// Inline cache operations (minimal subset)
const inlineCache = {
  get: (type: string, id: string) => {
    try {
      const key = `${type}_${id}`;
      const item = localStorage.getItem(key);
      if (!item) return null;
      const parsed = JSON.parse(item);
      if (parsed.expiry && Date.now() > parsed.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.data;
    } catch { return null; }
  },
  set: (type: string, id: string, data: any, ttl: number = 300000) => {
    try {
      const key = `${type}_${id}`;
      localStorage.setItem(key, JSON.stringify({
        data,
        expiry: Date.now() + ttl
      }));
    } catch {}
  },
  getBlueprint: (id: string) => inlineCache.get('blueprint', id),
  setBlueprint: (id: string, data: any) => inlineCache.set('blueprint', id, data),
  remove: (type: string, id: string) => {
    try {
      localStorage.removeItem(`${type}_${id}`);
    } catch {}
  },
  invalidateProject: (id: string) => {
    ['project-summary', 'blueprint', 'chapters'].forEach(type =>
      inlineCache.remove(type, id)
    );
  },
  invalidateBlueprint: (id: string) => inlineCache.remove('blueprint', id)
};

// Inline API service (minimal subset)
const inlineApi = {
  get: async (url: string) => {
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`);
      (error as any).response = { status: response.status };
      throw error;
    }
    return response.json();
  }
};

// Inline utilities (minimal subset)
const inlineHasChapters = (project: any): boolean => {
  return (project?.chapterCount ?? 0) > 0 ||
         (project?.projectData?.chapters?.length ?? 0) > 0;
};

const inlineChaptersCount = (project: any): number => {
  return project?.chapterCount ?? project?.projectData?.chapters?.length ?? 0;
};

const inlineHasBlueprint = (project: any): boolean => {
  return project?.hasBlueprint === true ||
         (project?.progress?.blueprint ?? 0) > 0 ||
         !!project?.projectData?.bookMetadata;
};

// Interfaces moved to @/writer/types to prevent circular imports

// Helper function to determine API calls needed per tab with smart chapter loading
function getRequiredAPICalls(tab: MainView, projectHasChapters?: boolean, projectHasBlueprint?: boolean): ('project' | 'blueprint' | 'chapters')[] {
  // Removed console.log to prevent spam - this is now controlled in useEffect
  switch (tab) {
    case 'ideaHub':
      // ✅ SMART LOADING: Load blueprint for ideaHub if it exists to show progress
      return projectHasBlueprint ? ['project', 'blueprint'] : ['project'];

    case 'bookDetails':
    case 'characters':
    case 'worldBuilding':
      return ['project', 'blueprint']; // 2 API calls needed

    case 'bookStructureStudio':
    case 'publishingEssentials':
    case 'bookPreview':
      // ✅ SMART LOADING: Load chapters if they exist for preview-dependent tabs
      return projectHasChapters ? ['project', 'blueprint', 'chapters'] : ['project', 'blueprint'];

    case 'chaptersBuilding':
      return ['project', 'blueprint', 'chapters']; // 3 API calls needed

    default:
      return ['project', 'blueprint']; // Default to blueprint loading for better caching
  }
}

export function useOptimizedStoryData(projectId: string | undefined, currentTab: MainView): OptimizedDataLoading {
  // 🔇 TRACE LOGGING: Disabled to prevent spam
  // Uncomment for debugging data loading issues
  // const lastLogRef = useRef<string>('');
  // const logKey = `${projectId}-${currentTab}`;
  // if (lastLogRef.current !== logKey && process.env.NODE_ENV === 'development') {
  //   console.log('🔍 TRACE: useOptimizedStoryData called with:', { projectId, currentTab });
  //   lastLogRef.current = logKey;
  // }

  // 1. ALL STATE DECLARATIONS FIRST (prevent TDZ errors)
  const [blueprintUpdated, setBlueprintUpdated] = useState(false);
  const [blueprintSaving, setBlueprintSaving] = useState(false);

  // 2. BASIC COMPUTATIONS (after state)
  const safeProjectId = projectId || '';
  const safeCurrentTab = currentTab || 'ideaHub';
  const isNumericId = safeProjectId ? !isNaN(Number(safeProjectId)) : false;
  const shouldDelayAPICalls = isNumericId;

  // 🔇 Security check logging (disabled to prevent spam)

  // 3. CALLBACKS (before any useMemo/useQuery that might depend on them)
  const handleBlueprintUpdated = useCallback((updated: boolean) => {
    setBlueprintUpdated(updated);
    // 🔇 Blueprint state change logging (disabled)
  }, []);

  const handleBlueprintSaving = useCallback((saving: boolean) => {
    setBlueprintSaving(saving);
    // 🔇 Blueprint state change logging (disabled)
  }, []);
  
  // Security warning for numeric IDs logged via useEffect in story-workspace.tsx
  
  // 1. Project data (summary) - always needed (baseline)
  const { 
    data: project, 
    isLoading: projectLoading, 
    error: projectError 
  } = useQuery({
    queryKey: ['story-project-summary', projectId], // ✅ OPTIMIZED: Use specific query key for summary
    queryFn: async () => {
      if (!projectId) return null;
      
      // Check cache first
      const cached = inlineCache.get('project-summary', projectId); // ✅ OPTIMIZED: Use specific cache key
      if (cached) {
        // ---- SAFE NORMALIZATION FOR CACHED DATA ----
        const safeTitle =
          cached?.title ??
          cached?.bookStructure?.currentProjectName ??
          'Untitled Project';

        const normalizedCached = {
          ...(cached ?? {}),
          title: cached?.title ?? safeTitle,
          bookStructure: {
            ...(cached?.bookStructure ?? {}),
            currentProjectName:
              cached?.bookStructure?.currentProjectName ??
              cached?.title ??
              safeTitle,
          },
          meta: {
            ...(cached?.meta ?? {}),
            updatedAt: cached?.meta?.updatedAt ?? new Date().toISOString(),
          },
        };

        // 🔇 Cache hit logging (disabled to prevent spam)
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('✅ Using cached project summary:', projectId);
        // }
        return normalizedCached;
      }
      
      try {
        // ✅ OPTIMIZED: Fetch from lightweight summary endpoint using apiService for 401 handling
        const data = await inlineApi.get(`/api/story-projects/${projectId}/summary`);
        // Cache for 5 minutes
        inlineCache.set('project-summary', projectId, data, 5 * 60 * 1000); // ✅ OPTIMIZED: Use specific cache key

        // ---- SAFE NORMALIZATION ----
        const safeTitle =
          data?.title ??
          data?.bookStructure?.currentProjectName ??
          'Untitled Project';

        const normalizedProject = {
          ...(data ?? {}),
          title: data?.title ?? safeTitle,
          // Ensure nested objects exist; avoid downstream null checks
          bookStructure: {
            ...(data?.bookStructure ?? {}),
            currentProjectName:
              data?.bookStructure?.currentProjectName ??
              data?.title ??
              safeTitle,
          },
          meta: {
            ...(data?.meta ?? {}),
            updatedAt: data?.meta?.updatedAt,
          },
        };

        // 🔇 API fetch logging (disabled to prevent spam)
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('✅ Fetched project summary from API:', projectId);
        // }

        return normalizedProject;
      } catch (error: any) {
        // ✅ FIX: Handle 404 summary responses gracefully
        if (error.response?.status === 404) {
          // Return minimal project structure to prevent breaking downstream logic
          return {
            id: null,
            uuid: projectId,
            title: "Loading...",
            hasChapters: false,
            chapterCount: 0,
            bookStructure: {
              currentProjectName: "Loading...",
            },
            meta: {
              updatedAt: new Date().toISOString(),
            },
          };
        }
        // For other errors, let apiService handle them (including logout for 401)
        throw error;
      }
    },
    enabled: !!projectId && !shouldDelayAPICalls, // Skip for numeric IDs to allow UUID redirect
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes (extended to prevent refetch after blueprint generation)
    refetchOnMount: false,     // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // 4. COMPUTED VALUES (after all queries are defined)
  // 🔇 Trace logging disabled

  const projectHasChapters = inlineHasChapters(project);
  const projectChaptersCount = inlineChaptersCount(project);
  const projectHasBlueprint = inlineHasBlueprint(project);

  const requiredCalls = getRequiredAPICalls(safeCurrentTab, projectHasChapters, projectHasBlueprint);

  // 2. Blueprint data loading (smart single API call)
  const blueprintEnabled = !!projectId && !shouldDelayAPICalls && requiredCalls.includes('blueprint');

  const {
    data: blueprintResponse,
    isLoading: blueprintLoading,
    error: blueprintError
  } = useQuery({
    queryKey: ['project-blueprint', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      
      // Check cache first
      const cached = inlineCache.getBlueprint(projectId);
      if (cached) {
        // 🔇 Cache hit logging (disabled to prevent spam)
        return {
          exists: true,
          loaded: true,
          data: cached,
          error: null
        };
      }
      
      try {
        const rawData = await inlineApi.get(`/api/story-projects/${projectId}/blueprint`);

        // ✅ TIMESTAMP SYNTHESIS: Add timestamps to blueprint if missing
        const now = new Date().toISOString();
        const data = {
          ...rawData,
          createdAt: rawData.createdAt ?? now,
          updatedAt: rawData.updatedAt ?? now,
        };

        // Cache until invalidated (24 hours default)
        inlineCache.setBlueprint(projectId, data);
        // 🔇 Blueprint caching logging (disabled to prevent spam)

        return {
          exists: true,
          loaded: true,
          data: data,
          error: null
        };
      } catch (error: any) {
        
        // Handle 404 (blueprint doesn't exist) - this is expected for new projects
        if (error.response?.status === 404) {
          return {
            exists: false,
            loaded: false,
            data: null,
            error: null
          };
        }
        
        // For other errors (like 401), let apiService handle the error (including logout)
        throw error;
      }
    },
    enabled: blueprintEnabled, // Load blueprint only when required by current tab
    staleTime: 10 * 60 * 1000, // Consider fresh for 10 minutes (extended to prevent refetch after blueprint generation)
    refetchOnMount: false,     // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // 4. Smart chapters data loading with caching hierarchy
  const {
    data: chapters,
    isLoading: chaptersLoading,
    error: chaptersError
  } = useQuery({
    queryKey: ['project-chapters', projectId], // Removed currentTab to prevent refetch on tab change
    queryFn: async () => {
      if (!projectId) return [];

      // ✅ ENHANCED CACHING: Check memory cache and localStorage
      const cacheKey = `chapters_${projectId}`;
      let cached = inlineCache.get('chapters', projectId);

      if (!cached) {
        try {
          const localStorageData = localStorage.getItem(cacheKey);
          if (localStorageData) {
            cached = JSON.parse(localStorageData);
            // 🔇 Restored from localStorage (logging disabled)
          }
        } catch (e) {
          console.warn('Failed to parse chapters from localStorage:', e);
        }
      }

      if (cached) {
        // ✅ FIX: Only return cached data if it's not an empty array from a previous 304 response
        if (Array.isArray(cached) && cached.length === 0) {
          // 🔇 Ignoring empty cache (logging disabled)
        } else {
          // 🔇 Using cached chapters (logging disabled)
          return cached;
        }
      }

      // ✅ OPTIMIZED: Always use the same endpoint regardless of tab (caching handles efficiency)
      const endpoint = `/api/projects/${projectId}/chapters`;

      // 🔇 Loading chapters from API (logging disabled)
      const rawResponse = await inlineApi.get(endpoint);

      // 🚀 SURGICAL FIX: Robust response shape normalization (shape-agnostic)
      const normalizedChapters =
        Array.isArray(rawResponse) ? rawResponse :
        Array.isArray(rawResponse.items) ? rawResponse.items :
        Array.isArray(rawResponse.chapters) ? rawResponse.chapters :
        Array.isArray(rawResponse.data) ? rawResponse.data :
        Array.isArray(rawResponse.data?.items) ? rawResponse.data.items :
        Array.isArray(rawResponse.data?.chapters) ? rawResponse.data.chapters :
        [];

      // 🔇 Chapter normalization logging (disabled)

      // ✅ TIMESTAMP SYNTHESIS: Add timestamps to chapters if missing
      const now = new Date().toISOString();
      const data = normalizedChapters.map((chapter: any) => ({
        ...chapter,
        createdAt: chapter.createdAt ?? now,
        updatedAt: chapter.updatedAt ?? now,
      }));

      // ✅ FIX: Only cache non-empty chapter arrays to prevent empty cache pollution
      if (data && Array.isArray(data) && data.length > 0) {
        // Cache the actual chapter data
        inlineCache.set('chapters', projectId, data, 10 * 60 * 1000);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
          // 🔇 Cached chapters (logging disabled)
        } catch (e) {
          console.warn('Failed to cache chapters to localStorage:', e);
        }
      }
      // 🔇 Empty chapters array handling (logging disabled)

      return data || [];
    },
    enabled: !!projectId && !shouldDelayAPICalls && safeCurrentTab !== 'ideaHub', // Load chapters for all tabs except ideaHub
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
  });

  // ✅ STORY-ONLY LOADING: Drive isLoading from the three story queries only
  const isLoading = shouldDelayAPICalls ? false : (
    (requiredCalls.includes('project') && projectLoading) ||
    (requiredCalls.includes('blueprint') && blueprintLoading) ||
    (requiredCalls.includes('chapters') && chaptersLoading)
  );

  // ✅ CRITICAL FIX: Memoize errors object to prevent infinite re-render loops
  const errors: OptimizedDataLoading['errors'] = useMemo(() => {
    const errs: OptimizedDataLoading['errors'] = {};
    if (projectError) errs.project = projectError.message;
    if (blueprintError) errs.blueprint = blueprintError.message;
    if (chaptersError) errs.chapters = chaptersError.message;
    return errs;
  }, [projectError?.message, blueprintError?.message, chaptersError?.message]);

  // Construct blueprint state from response
  const blueprintState: BlueprintState = useMemo(() => ({
    blueprintExists: blueprintResponse?.exists || false,
    blueprintLoaded: blueprintResponse?.loaded || false,
    blueprintData: blueprintResponse?.data || null,
    blueprintUpdated: blueprintUpdated, // Managed by local state
    blueprintSaving: blueprintSaving,   // Managed by local state
    blueprintError: blueprintResponse?.error || null,
    setBlueprintUpdated: handleBlueprintUpdated,
    setBlueprintSaving: handleBlueprintSaving,
  }), [
    blueprintResponse?.exists,
    blueprintResponse?.loaded,
    blueprintResponse?.data,
    blueprintResponse?.error,
    blueprintUpdated,
    blueprintSaving,
    handleBlueprintUpdated,
    handleBlueprintSaving
  ]);

  // ✅ CONTROLLED OPTIMIZATION LOGGING: Prevent spam
  // ✅ STABLE DECISION INPUTS: Now safe to compute after all queries are defined
  const decisionInputs = useMemo(() => JSON.stringify({
    view: safeCurrentTab,
    projectId: safeProjectId,
    blueprintExists: blueprintResponse?.exists ?? false,
    chaptersCount: project?.chapterCount ?? 0,
    projectUpdatedAt: project?.meta?.updatedAt ?? null,
  }), [safeCurrentTab, safeProjectId, blueprintResponse?.exists, project?.chapterCount, project?.meta?.updatedAt]);

  const optimizationKey = useMemo(() => JSON.stringify({
    currentTab: safeCurrentTab,
    requiredCalls: requiredCalls.length,
    isLoading,
    projectLoaded: !!project,
    blueprintLoaded: blueprintState.blueprintLoaded,
    chaptersLoaded: !!chapters
  }), [safeCurrentTab, requiredCalls.length, isLoading, project, blueprintState.blueprintLoaded, chapters]);

  useEffect(() => {
    // if ((window as any).__lastOptimizationKey !== optimizationKey) {
    //   console.log(`📊 API Optimization for ${currentTab}:`, {
    //     shouldDelayAPICalls,
    //     requiredCalls: requiredCalls.length,
    //     isLoading,
    //     loadingStates: {
    //       projectLoading,
    //       blueprintLoading: requiredCalls.includes('blueprint') ? blueprintLoading : 'skipped',
    //       chaptersLoading: requiredCalls.includes('chapters') ? chaptersLoading : 'skipped'
    //     },
    //     actualCalls: [
    //       projectLoading ? 'project-loading' : project ? 'project-cached' : 'project-none',
    //       requiredCalls.includes('blueprint') ? (blueprintLoading ? 'blueprint-loading' : blueprintState.blueprintLoaded ? 'blueprint-cached' : 'blueprint-none') : 'blueprint-skipped',
    //       requiredCalls.includes('chapters') ? (chaptersLoading ? 'chapters-loading' : chapters ? 'chapters-cached' : 'chapters-none') : 'chapters-skipped'
    //     ].filter(call => !call.includes('skipped')),
    //     blueprintState: {
    //       exists: blueprintState.blueprintExists,
    //       loaded: blueprintState.blueprintLoaded,
    //       hasData: !!blueprintState.blueprintData
    //     }
    //   });
    //   (window as any).__lastOptimizationKey = optimizationKey;
    // }
  }, [optimizationKey, shouldDelayAPICalls, requiredCalls.length, isLoading, projectLoading, blueprintLoading, chaptersLoading, project, blueprintState, chapters, currentTab]);

  // ✅ CRITICAL FIX: Memoize return object to prevent infinite re-render loops
  const finalResult = useMemo(() => ({
    project: project || null,
    blueprint: blueprintState,
    chapters: chapters || [],
    isLoading,
    errors,
  }), [project, blueprintState, chapters, isLoading, errors]);

  // 🔇 Return value logging (disabled to prevent spam)
  // Uncomment for debugging data loading issues
  // const lastReturnLogRef = useRef<string>('');
  // const returnLogKey = JSON.stringify({
  //   hasProject: !!project,
  //   blueprintExists: blueprintState.blueprintExists,
  //   chaptersCount: chapters?.length || 0,
  //   isLoading
  // });
  // if (lastReturnLogRef.current !== returnLogKey && process.env.NODE_ENV === 'development') {
  //   console.log('🔍 TRACE: useOptimizedStoryData returning:', finalResult);
  //   lastReturnLogRef.current = returnLogKey;
  // }

  return finalResult;
}

// Cache invalidation helpers
export const invalidateProjectCache = (projectId: string) => {
  inlineCache.invalidateProject(projectId);
  // ✅ OPTIMIZED: Invalidate project summary cache as well
  inlineCache.remove('project-summary', projectId);
};

export const invalidateBlueprintCache = (projectId: string) => {
  inlineCache.invalidateBlueprint(projectId);
  inlineCache.remove('blueprint_exists', projectId);
};